import * as endpoints from '@App/constants/endpoints';
import * as util from '@Util/';
import interceptor from '../interceptor';

import { post } from '../util';

const DOCUMENT_AI = 'document-ai';
const RUN = 'run';

export const run = async ({ header, params }) => {
  const pathUrl = `${endpoints.API_DOCS}/${DOCUMENT_AI}/${RUN}`;
  const axios = interceptor(header);
  return await post(axios, pathUrl, params);
};

const buildStreamHeaders = (props = {}, options = {}) => {
  const auth = JSON.parse(util.getCookie('auth') || '{}');
  const headers = {
    Accept: 'text/event-stream',
  };

  if (!options.multipart) {
    headers['Content-Type'] = 'application/json';
  }

  if (auth && auth.hasOwnProperty('token')) {
    headers.Authorization = `Bearer ${auth.token}`;
  }

  if (props && props.hasOwnProperty('token') && props.token) {
    headers.Authorization = `Bearer ${props.token}`;
  }

  if (props && props.hasOwnProperty('account') && props.account) {
    headers.Account = props.account;
  }

  if (props && props.hasOwnProperty('accountId') && props.accountId) {
    headers.Account = props.accountId;
  }

  if (props && props.hasOwnProperty('workspace') && props.workspace) {
    headers.Workspace = props.workspace;
  }

  if (props && props.hasOwnProperty('workspaceId') && props.workspaceId) {
    headers.Workspace = props.workspaceId;
  }

  if (props && props.hasOwnProperty('groupPermission')) {
    headers['group-permission'] = props.groupPermission;
  }

  if (props && props.hasOwnProperty('formOrigin')) {
    headers['form-origin'] = props.formOrigin;
  }

  if (props && props.hasOwnProperty('userId')) {
    headers.user = props.userId;
  }

  if (props && props.hasOwnProperty('medicalRecordsAttendance')) {
    headers['medical-records-attendance'] = props.medicalRecordsAttendance;
  }

  let lang = navigator.language || navigator.userLanguage;
  if (auth && auth.languageCode) {
    lang = auth.languageCode;
  }

  if (lang) {
    headers['x-lang'] = lang.startsWith('pt') ? lang : lang.split('-')[0];
  }

  return headers;
};

const buildRequestPayload = (params = {}) => {
  const { files = [], onChunk, ...payload } = params;

  if (!files.length) {
    return {
      body: JSON.stringify(payload),
      multipart: false,
    };
  }

  const formData = new FormData();
  formData.append('payload', JSON.stringify(payload));
  files.forEach((file) => formData.append('files', file));

  return {
    body: formData,
    multipart: true,
  };
};

const readJsonResponse = async (response) => {
  const body = await response.json();
  return body && body.data ? body.data : body;
};

const normalizeSsePayload = (payload) => {
  if (!payload || payload === '[DONE]') return {};

  try {
    return JSON.parse(payload);
  } catch (error) {
    return { chunk: payload };
  }
};

const getChunkText = (event) => {
  if (!event || event.done) return '';
  if (typeof event === 'string') return event;
  return event.chunk || event.delta || event.text || event.content || event.message || '';
};

const normalizeErrorText = (error = {}) => String(
  error.userFeedback ||
  error.message ||
  (error.response && error.response.userFeedback) ||
  (error.response && error.response.message) ||
  ''
).toLowerCase();

export const isDocumentAiPlanError = (error = {}) => {
  const text = normalizeErrorText(error);
  return (
    error.status === 403 ||
    text.includes('plano') ||
    text.includes('plan') ||
    text.includes('permiss') ||
    text.includes('permission') ||
    text.includes('não liber') ||
    text.includes('nao liber') ||
    text.includes('not allowed') ||
    text.includes('not enabled')
  );
};

export const isDocumentAiInsufficientTokensError = (error = {}) => {
  const text = normalizeErrorText(error);
  return (
    (text.includes('token') && (
      text.includes('insuf') ||
      text.includes('insuff') ||
      text.includes('not enough') ||
      text.includes('não há') ||
      text.includes('nao ha') ||
      text.includes('sem ')
    )) ||
    text.includes('crédito insuficiente') ||
    text.includes('creditos insuficientes') ||
    text.includes('créditos insuficientes') ||
    text.includes('insufficient credit') ||
    text.includes('insufficient token')
  );
};

const processSseEvent = (rawEvent, onChunk) => {
  const data = rawEvent
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.replace(/^data:\s?/, ''))
    .join('\n');

  const event = normalizeSsePayload(data);

  if (event.error) {
    const error = new Error(event.userFeedback || event.message || 'Document AI stream failed.');
    error.response = event;
    error.status = event.status;
    error.userFeedback = event.userFeedback || event.message;
    error.isPlanError = isDocumentAiPlanError(error);
    error.isInsufficientTokens = isDocumentAiInsufficientTokensError(error);
    throw error;
  }

  if (event.done) {
    return event.data || {};
  }

  const chunk = getChunkText(event);
  if (chunk && onChunk) onChunk(chunk);

  return null;
};

export const runStream = async ({ header, params, onChunk }) => {
  const pathUrl = `${endpoints.API_DOCS}/${DOCUMENT_AI}/${RUN}`;
  const requestPayload = buildRequestPayload(params);
  const response = await fetch(pathUrl, {
    method: 'POST',
    headers: buildStreamHeaders(header, { multipart: requestPayload.multipart }),
    body: requestPayload.body,
  });

  if (!response.ok) {
    let responseBody = {};
    try {
      responseBody = await response.json();
    } catch (error) {
      responseBody = {};
    }

    throw {
      status: response.status,
      response: responseBody,
      userFeedback: responseBody.userFeedback,
      isPlanError: isDocumentAiPlanError({ status: response.status, response: responseBody, userFeedback: responseBody.userFeedback }),
      isInsufficientTokens: isDocumentAiInsufficientTokensError({ status: response.status, response: responseBody, userFeedback: responseBody.userFeedback }),
    };
  }

  const contentType = response.headers.get('content-type') || '';
  if (!response.body || !contentType.includes('text/event-stream')) {
    return readJsonResponse(response);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finalData = {};

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';

    events.forEach((rawEvent) => {
      const data = processSseEvent(rawEvent, onChunk);
      if (data) finalData = data;
    });
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    const data = processSseEvent(buffer, onChunk);
    if (data) finalData = data;
  }

  return finalData;
};
